import React from "react";
import { TextField, MenuItem, FormHelperText, IconButton, InputAdornment, Box } from "@mui/material";
import { Clear } from "@mui/icons-material";

interface FormSelectProps {
  label: string;
  value: any;
  options: any[];
  onChange: (value: any, selectedObj: any) => void;
  getOptionValue?: (item: any) => any;
  getOptionLabel?: (item: any) => string;
  getOptionDisabled?: (item: any) => boolean;
  getOptionPrefix?: (item: any) => React.ReactNode;
  getOptionPostfix?: (item: any) => React.ReactNode;
  helperText?: string;
  sx?: any;
  primaryColor?: string;
  clearable?: boolean;
  disabled?: boolean;
}

const FormSelect: React.FC<FormSelectProps> = ({
  label,
  value,
  options,
  onChange,
  getOptionValue = (item) => item.value,
  getOptionLabel = (item) => item.label,
  getOptionDisabled = () => false,
  getOptionPrefix = undefined,
  getOptionPostfix = undefined,
  helperText,
  sx = {},
  primaryColor = "#0047C0",
  clearable = false,
  disabled = false,
}) => {
  const handleChange = (event: any) => {
    const selectedValue = event.target.value;
    const selectedObj = options.find(
      (item) => getOptionValue(item) === selectedValue
    );
    onChange(selectedValue, selectedObj);
  };

  const handleClear = () => {
    onChange("", null);
  };

  return (
    <div>
      <TextField
        select
        fullWidth
        label={label}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        InputProps={{
          endAdornment: clearable && value ? (
            <InputAdornment position="end">
              <IconButton
                onClick={handleClear}
                size="small"
                sx={{
                  marginRight: 1,
                  color: '#666',
                  '&:hover': {
                    color: primaryColor,
                    backgroundColor: 'rgba(0, 71, 192, 0.04)',
                  },
                }}
              >
                <Clear fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
        SelectProps={{
          autoFocus: false,
          MenuProps: {
            autoFocus: false,
            disableAutoFocusItem: true,
            PaperProps: {
              sx: {
                maxWidth: '90vw',     // keeps dropdown inside screen
                overflowX: 'hidden',
                maxHeight: 280,
              },
            },
          },
        }}
        sx={{
          backgroundColor: "#fff",
          borderRadius: 1,
          "& .MuiOutlinedInput-root": {
            "& .MuiOutlinedInput-notchedOutline": {
              border: "1px solid #ccc !important",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: `${primaryColor} !important`,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: `${primaryColor} !important`,
              borderWidth: '2px !important',
            },
          },
          "& .MuiInputLabel-root.Mui-focused": {
            color: `${primaryColor} !important`, // floating label color on focus
          },
          "& .MuiInputBase-input": { color: "#000" },
          ...sx,
        }}
      >
        {options.map((opt, index) => {
          const isDisabled = getOptionDisabled(opt);
          return (
            <MenuItem
              sx={{  
                whiteSpace: "normal",    // allow multi-line wrapping
                wordWrap: "break-word",
                lineHeight: 1.3, 
                paddingTop: 1.5, 
                paddingBottom: 1.5, 
                paddingLeft: 2, 
                paddingRight: 2,
                borderBottom: index !== options.length - 1 ? "1px solid #e5e5e5" : "none",
                opacity: isDisabled ? 1 : 1,
                color: isDisabled ? '#000' : 'inherit',
                pointerEvents: isDisabled ? 'none' : 'auto',
              }}
              key={getOptionValue(opt)} 
              value={getOptionValue(opt)}
              disabled={isDisabled}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                {getOptionPrefix && getOptionPrefix(opt)}
                <Box sx={{ flex: 1 }}>
                  {getOptionLabel(opt)}
                </Box>
                {getOptionPostfix && getOptionPostfix(opt)}
              </Box>
            </MenuItem>
          );
        })}
      </TextField>
      {helperText && (
        <FormHelperText sx={{ color: "#f44336", marginLeft: "4px", fontSize: "13px" }}>
          {helperText}
        </FormHelperText>
      )}
    </div>
  );
};

export default FormSelect;
